import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LOCALES } from '../i18n';
import { ThemeToggle } from '../components/ThemeToggle';
import { Tooltip } from '../components/Tooltip';
import { NewsletterForm } from '../components/NewsletterForm';

export const Route = createFileRoute('/')({
  component: HomeRoute,
});

function MountainIcon() {
  return (
    <svg
      width='88'
      height='60'
      viewBox='0 0 88 60'
      className='text-accent'
      aria-hidden
      fill='currentColor'
    >
      {/* Back-left peak */}
      <polygon points='4,56 24,22 44,56' opacity='0.22' />
      {/* Back-right peak */}
      <polygon points='44,56 66,16 88,56' opacity='0.28' />
      {/* Main center peak */}
      <polygon points='14,56 42,4 70,56' />
      {/* Snow cap */}
      <polygon points='42,4 34,22 50,22' opacity='0.38' />
    </svg>
  );
}

/** Subtle mountain range silhouette across the bottom of the hero */
function MountainBackground() {
  return (
    <svg
      viewBox='0 0 1200 260'
      className='pointer-events-none absolute bottom-0 left-0 w-full text-accent'
      aria-hidden
      preserveAspectRatio='xMidYMax meet'
      fill='currentColor'
    >
      {/* Far layer */}
      <polygon points='0,260 180,110 360,260' opacity='0.05' />
      <polygon points='240,260 460,70 680,260' opacity='0.05' />
      <polygon points='580,260 800,95 1020,260' opacity='0.05' />
      <polygon points='860,260 1060,120 1200,260' opacity='0.05' />
      {/* Mid layer */}
      <polygon points='0,260 160,145 320,260' opacity='0.07' />
      <polygon points='260,260 480,85 700,260' opacity='0.07' />
      <polygon points='550,260 780,105 1010,260' opacity='0.07' />
      <polygon points='880,260 1080,135 1200,260' opacity='0.06' />
      {/* Near layer */}
      <polygon points='0,260 140,175 280,260' opacity='0.1' />
      <polygon points='200,260 420,130 640,260' opacity='0.09' />
      <polygon points='500,260 720,115 940,260' opacity='0.09' />
      <polygon points='820,260 1020,160 1200,260' opacity='0.1' />
    </svg>
  );
}

function LanguageSwitcher() {
  const { t, i18n } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} className='relative'>
      <Tooltip content={t('locale.switchLabel')}>
        <button
          type='button'
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={t('locale.switchLabel')}
          className='flex h-9 w-9 items-center justify-center rounded-full border border-border bg-bg text-fg-muted shadow-elevated transition-colors hover:border-accent-border hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg'
        >
          <svg
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.75'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden
          >
            <circle cx='12' cy='12' r='10' />
            <path d='M12 2a14.5 14.5 0 0 0 0 20M12 2a14.5 14.5 0 0 1 0 20' />
            <path d='M2 12h20' />
          </svg>
        </button>
      </Tooltip>

      {open && (
        <div
          role='menu'
          className='absolute right-0 top-11 min-w-[7rem] overflow-hidden rounded-lg border border-border bg-bg shadow-elevated'
        >
          {SUPPORTED_LOCALES.map((code) => (
            <button
              key={code}
              type='button'
              role='menuitem'
              onClick={() => {
                void i18n.changeLanguage(code);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm transition-colors hover:bg-surface focus:outline-none ${
                i18n.language.startsWith(code)
                  ? 'font-semibold text-accent'
                  : 'font-normal text-fg-muted'
              }`}
            >
              {i18n.language.startsWith(code) && (
                <span aria-hidden className='text-accent'>
                  ▲
                </span>
              )}
              {!i18n.language.startsWith(code) && (
                <span className='w-3' aria-hidden />
              )}
              {code === 'en' ? t('locale.en') : t('locale.pl')}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function HomeRoute() {
  const { t } = useTranslation('common');

  return (
    <main className='relative flex min-h-0 w-full flex-1 flex-col items-center justify-center overflow-hidden px-6 py-20'>
      {/* Soft radial glow */}
      <div className='pointer-events-none absolute inset-0 -z-10'>
        <div className='absolute left-1/2 top-[40%] h-[30rem] w-[44rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent-muted blur-3xl' />
      </div>

      {/* Mountain silhouette layers at the bottom */}
      <MountainBackground />

      <div className='absolute right-6 top-6 flex items-center gap-2'>
        <ThemeToggle />
        <LanguageSwitcher />
      </div>

      <div className='relative flex flex-col items-center gap-5 text-center'>
        {/* Mountain icon */}
        <MountainIcon />

        {/* Brand heading */}
        <h1 className='font-heading text-[5.5rem] font-bold leading-none tracking-[-0.04em] text-fg sm:text-[7.5rem] md:text-[10rem]'>
          {t('brand.name')}
        </h1>

        {/* Description */}
        <div className='mt-2 space-y-1'>
          <p className='text-xl font-semibold text-fg sm:text-2xl'>
            {t('landing.description')}
          </p>
          <p className='text-lg text-fg-muted sm:text-xl'>
            {t('landing.tagline')}
          </p>
        </div>

        {/* Decorative divider */}
        <div className='mt-4 flex items-center gap-4'>
          <span
            className='h-px w-14 rounded-full bg-accent-border'
            aria-hidden
          />
          <span className='text-base text-accent' aria-hidden>
            ▲
          </span>
          <span
            className='h-px w-14 rounded-full bg-accent-border'
            aria-hidden
          />
        </div>

        {/* Newsletter signup */}
        <div className='mt-2 flex w-full flex-col items-center gap-3'>
          <p className='text-sm text-fg-muted'>{t('newsletter.label')}</p>
          <NewsletterForm />
        </div>
      </div>
    </main>
  );
}
